import { TaskStage } from '../types/Task';

/**
 * Determines if fields should be locked based on task state and user permissions
 */
export interface FieldLockingConfig {
  taskStatus?: TaskStage;
  userRole?: string;
  hasEditInternalReviewPermission?: boolean;
  hasEditClosedFinalizedPermission?: boolean;
}

/**
 * Field locking rules:
 * - Closed, Cancelled, and Finalized: all fields locked for all roles
 * - Internal Review: all fields locked except for Ops Manager and Admins (with Edit Internal Review permission)
 */
export const getFieldLockingState = (config: FieldLockingConfig) => {
  const { taskStatus, hasEditInternalReviewPermission = false } = config;

  // Closed, Cancelled, and Finalized - all fields locked for everyone
  if (taskStatus === TaskStage.CLOSED || taskStatus === TaskStage.CANCELLED || taskStatus === TaskStage.FINALIZED) {
    return {
      isLocked: true,
      reason: 'Task is in Closed, Cancelled, or Finalized state',
      canEdit: false
    };
  }

  // Internal Review - locked for everyone except Ops Manager and Admins with permission
  if (taskStatus === TaskStage.INTERNAL_REVIEW) {
    return {
      isLocked: !hasEditInternalReviewPermission,
      reason: hasEditInternalReviewPermission 
        ? 'You have permission to edit tasks in Internal Review' 
        : 'Task is in Internal Review state - only Ops Manager and Admins can edit',
      canEdit: hasEditInternalReviewPermission
    };
  }

  // All other states - fields are editable
  return {
    isLocked: false,
    reason: 'Task is in editable state',
    canEdit: true
  };
};

/**
 * Get the appropriate style for locked fields
 */
export const getLockedFieldStyle = (isLocked: boolean) => {
  return isLocked ? {
    backgroundColor: '#e9ecef',
    cursor: 'not-allowed',
    opacity: 0.7
  } : undefined;
};

/**
 * Get the appropriate style for locked button labels and UI elements
 * This version doesn't reduce opacity to maintain text visibility
 */
export const getLockedButtonStyle = (isLocked: boolean) => {
  return isLocked ? {
    cursor: 'not-allowed',
    opacity: 0.8
  } : undefined;
};

/**
 * Get the appropriate style for locked active/selected button labels
 * This preserves the active state (blue) while making it locked
 */
export const getLockedActiveButtonStyle = (isLocked: boolean) => {
  return isLocked ? {
    cursor: 'not-allowed',
    opacity: 0.8
  } : undefined;
};

/**
 * Check if user has permission to edit tasks in Internal Review
 * This should be used with the usePermissions hook
 */
export const canEditInternalReview = (hasPermission: (permission: string) => boolean) => {
  // Check for specific permission for editing tasks in Internal Review
  return hasPermission('quantified:edit_internal_review') ||
         hasPermission('quantified:review_manage') || 
         hasPermission('quantified:actual_accomplishment_edit') ||
         hasPermission('role:edit'); // Admin permission
};
